/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Moment } from 'moment';
import { IRequestCancellable, IRequestCustomConfig, IRequestError } from 'projects/common/src/app/interfaces/communication/request';
import { IRequestResponse } from 'projects/common/src/app/interfaces/communication/response';
import { ERequestState } from 'projects/communication/src/app/enumerables/request-state';
import { Observable, Subject } from 'rxjs';
import { debounceTime, delay, expand, filter, finalize, take, takeUntil, takeWhile } from 'rxjs/operators';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { IStoredProcedureInputs } from 'src/app/modules/dashboard/interfaces/chart-widget';
import { CommunicationService } from 'src/app/services/communication';
import { environment } from 'src/environments/environment';
import { ParameterHandlerService } from '../../../../../../projects/common/src/app/services';
import { getMoment } from '../../../core/services/datetime.service';
import { ScreenViewService } from '../../services/screen-view.service';
import { WidgetBasicComponent } from '../widget-basic/widget-basic.component';

@Component({
  selector: 'app-widget-request',
  templateUrl: './widget-request.component.html',
  styleUrls: ['./widget-request.component.scss'],
})
export class WidgetRequestComponent extends WidgetBasicComponent implements OnInit, OnDestroy {
  public readonly delay = environment.timeout;

  public requestState: ERequestState = ERequestState.standby;
  protected dataRequest: Subject<boolean>;
  protected request: IRequestCancellable;
  private initialized = false;

  private firstCall: boolean;
  public isWidgetOnScreen = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected screenViewService: ScreenViewService,
    protected parameterService?: ParameterHandlerService,
  ) {
    super();
    this.dataRequest = new Subject();
    this.firstCall = true;
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelCurrentRequest();
    this.componentDestroyed = true;
  }

  public cancelCurrentRequest(): void {
    if (this.dataRequest) {
      this.dataRequest.next();
      this.dataRequest.complete();
      this.dataRequest = new Subject();
    }
  }

  protected buildRequestParams(): IStoredProcedureInputs {
    // Diccionario para get o post a enviar
    return null;
  }

  protected buildRequestOptions(): IRequestCustomConfig {
    return {
      timeout: this.data.timeout,
    };
  }

  public ngOnInit(): void {
    this.screenViewService.widgetCancelRequest
      .pipe(
        filter((widget) => widget.id === this.data.componentId), // Ensure that the request is for the current widget
        debounceTime(100), // Adjust the time to avoid multiple requests, set as needed
      )
      .subscribe((widget) => {
        if (this.data.componentId === widget.id) {
          if (widget.onScreen) {
            this.initializeRemoteRequest(true);
          } else {
            this.cancelCurrentRequest(); // Ensure that the request is cancelled
          }
        }
      });

    // verifies if the widget is visible on the screen, then initializes the request.
    const isVisible = this.screenViewService.widgetVisibilityState[this.data.componentId];
    if (isVisible && !this.initialized) {
      this.initializeRemoteRequest();
    }
  }

  protected initializeRemoteRequest(forceReinitialize = false): void {
    if (!forceReinitialize && this.initialized) {
      return; // avoid memory leaks
    }

    this.initialized = true;

    if (this.componentDestroyed) {
      return; // avoid memory leaks
    }

    this.requestState = ERequestState.pending;

    this.performCall()
      .pipe(
        take(1),
        takeWhile(() => !this.componentDestroyed),
      )
      .subscribe(
        (result: IRequestResponse) => {
          const hasData = this.checkIfResultHasData(result);

          if (hasData) {
            this.buildView(result);
            this.requestState = ERequestState.standby;
          } else {
            this.handleEmptyData();
          }
        },

        (err: HttpErrorResponse) => {
          this.handleRequestError(err);
        },
      );
  }

  private checkIfResultHasData(result: any): boolean {
    if (Array.isArray(result)) {
      return result.length > 0;
    }

    if (typeof result === 'object' && result !== null) {
      return Object.values(result).some((value) => Array.isArray(value) && value.length > 0);
    }

    return false;
  }

  protected handleEmptyData(): void {
    this.updatedAt.emit(new Date());
    this.noData.emit(true);
  }

  /**
   * Handles the error of the HTTP request.
   */
  private handleRequestError(err: HttpErrorResponse): void {
    this.requestError = {
      title: err.statusText,
      message: err.message,
      code: err.status,
      error: err.statusText,
    } as IRequestError;
    this.updatedAt.emit(new Date());
    this.errorOccurred.emit(this.requestError);
    this.requestState = ERequestState.error;
  }

  protected recursiveCall(): Observable<any> {
    return this.performCall().pipe(
      expand(() => {
        this.firstCall = false;
        return this.performCall().pipe(delay(this.data ? this.data.delay : this.delay));
      }),
    );
  }

  /**
   * Makes the call to the API to get the data.
   */
  protected performCall(): Observable<any> {
    const params = this.buildRequestParams();
    const options = this.buildRequestOptions();
    this.progressBarService.start();
    return this.communication.get(this.data.source, params, options).pipe(finalize(() => this.progressBarService.stop()));
  }

  /**
   * Used to build the view of the inherited component.
   * @param data = Data to build the view of each component.
   */
  protected buildView(data?: any): void {
    this.updatedAt.emit(new Date());
  }
}
